import { useLocation, useNavigate } from "react-router-dom";
import { persistChallengeGameEntryFromNotification } from "../../utils/challengeEntryTracking";
import { ProfileLink } from "../Profile/ProfileLink";
import { AvatarWithXpCrown, AVATAR_PRESENCE_INDICATOR_Z_CLASS } from "../Xp/AvatarWithXpCrown";
import { UserAvatar } from "../Profile/UserAvatar";
import { resolveAvatarFrameSrc } from "../Profile/avatarFrame";
import { useProfileGlobalModal } from "../Profile/ProfileGlobalModalContext";
import { isProfileHref, profileModalBackgroundState } from "../../utils/profileModalNavigation";
import {
  formatTimeAgo,
  getNotificationDisplay,
  getNotificationRowGridShellClass,
  getNotificationRowShellClass,
  MY_ZONE_ACTIVITY_PANEL_AVATAR_CLASS,
  MY_ZONE_ACTIVITY_PANEL_CTA_CLASS,
  MY_ZONE_ACTIVITY_PANEL_ROW_GRID_CLASS,
  MY_ZONE_ACTIVITY_PANEL_TIME_CLASS,
  MY_ZONE_ACTIVITY_PANEL_TITLE_LINE_CLASS,
  MY_ZONE_ACTIVITY_PANEL_TITLE_USERNAME_CLASS,
  NOTIFICATION_AVATAR_CLASS,
  NOTIFICATION_CTA_PILL_CLASS,
  NOTIFICATION_MAIN_TEXT_PAD,
  notificationSenderAppearsOnline,
} from "./notificationDisplay";

const NOTIFICATION_SENDER_ONLINE_DOT_CLASS =
  `pointer-events-none absolute -right-0.5 -top-0.5 ${AVATAR_PRESENCE_INDICATOR_Z_CLASS} h-2 w-2 rounded-full border border-[#1B062F] bg-[#d4ff1f] shadow-[0_0_0_1px_rgba(27,6,47,0.35),0_2px_4px_rgba(0,0,0,0.45)]`;

function NotificationRowAvatar({ boxClass, src, imgClass, notification, xpWeekCrown, isSenderAvatar, frameSize }) {
  const showOnline = notificationSenderAppearsOnline(notification?.sender);
  const sender = notification?.sender;
  const displayName =
    sender?.player_name || sender?.playerName || sender?.username || notification?.sender_name || null;
  const frameSrc = isSenderAvatar
    ? resolveAvatarFrameSrc(frameSize, sender?.avatar_frame_url, sender?.avatar_frame_small_url)
    : null;
  return (
    <div className={boxClass}>
      <AvatarWithXpCrown crown={xpWeekCrown} frameSrc={frameSrc} className="block h-full w-full overflow-hidden">
        <UserAvatar
          src={src}
          displayName={displayName}
          size="fill"
          imgClassName={imgClass}
          withCrown={false}
        />
      </AvatarWithXpCrown>
      {showOnline ? <span className={NOTIFICATION_SENDER_ONLINE_DOT_CLASS} aria-hidden /> : null}
    </div>
  );
}

const RUBY_ICON_SRC = "/assets/currency-ruby.png";
const COIN_ICON_SRC = "/assets/currency-gold-ball.png";
const XP_ICON_SRC = "/assets/xp.webp";

const INLINE_CURRENCY_ICON_CLASS =
  "mx-px inline-block h-[1.05em] w-[1.05em] max-h-[15px] max-w-[15px] shrink-0 object-contain align-[-0.2em] sm:max-h-[16px] sm:max-w-[16px]";

function NotificationRemoveButton({ onRemove }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onRemove();
      }}
      className="mt-0.5 flex h-[30px] w-[30px] shrink-0 items-center justify-center self-start rounded-none border-0 bg-transparent text-white/60 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
      aria-label="Remove notification"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4"
        aria-hidden
      >
        <path d="M3 6h18" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <line x1="10" y1="11" x2="10" y2="17" />
        <line x1="14" y1="11" x2="14" y2="17" />
      </svg>
    </button>
  );
}

/** Body: Chakra Petch 400, 12–13px; friend username: Anton 400, 10–11px uppercase. Optional single-line clamp. */
function NotificationTitleLine({
  parts,
  lineClamp = true,
  lineClass: lineClassOverride,
  emphasizeUsernameClass: emphasizeUsernameClassOverride,
}) {
  const lineClass =
    lineClassOverride ??
    [
      "!block w-full min-w-0 font-chakra text-[12px] font-normal leading-[18px] tracking-normal text-white sm:text-[13px] sm:leading-[20px]",
      lineClamp ? "line-clamp-1 min-w-0" : "whitespace-normal break-words",
    ].join(" ");
  const emphasizeUsernameClass =
    emphasizeUsernameClassOverride ??
    "font-anton text-[10px] font-normal leading-[18px] tracking-normal uppercase sm:text-[11px] sm:leading-[20px]";
  return (
    <p className={lineClass}>
      {parts.map((part, i) => {
        if (part.coinIcon) {
          return (
            <img
              key={i}
              src={COIN_ICON_SRC}
              alt=""
              className={INLINE_CURRENCY_ICON_CLASS}
              aria-hidden
            />
          );
        }
        if (part.rubyIcon) {
          return (
            <img
              key={i}
              src={RUBY_ICON_SRC}
              alt=""
              className={INLINE_CURRENCY_ICON_CLASS}
              aria-hidden
            />
          );
        }
        if (part.xpIcon) {
          return (
            <img
              key={i}
              src={XP_ICON_SRC}
              alt=""
              className={INLINE_CURRENCY_ICON_CLASS}
              aria-hidden
            />
          );
        }
        const t = part.text ?? "";
        if (part.emphasizeUsername) {
          return (
            <span key={i} className={emphasizeUsernameClass}>
              {t}
            </span>
          );
        }
        return <span key={i}>{t}</span>;
      })}
    </p>
  );
}

/**
 * Single notification row — same layout as NotificationsDrawer list items / toast / activity notifications.
 * @param {boolean} [includeUnreadRing=true] — set false for toast alerts (no inset white ring).
 * @param {string} [rowShellClassName] — optional override for row container classes.
 * @param {boolean} [hideTime=false] — omit relative time in the side rail / subline.
 */
export function NotificationItemRow({
  notification,
  isUnread = true,
  onActivate,
  onRemove,
  includeUnreadRing = true,
  rowShellClassName,
  hideTime = false,
  isAlertRow = false,
  /** My Zone desktop activity panel typography and row sizing. */
  variant = "default",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { openProfileFromHref } = useProfileGlobalModal();
  const { titleParts, avatarUrl, cta, navigateTo, avatarObjectFit } = getNotificationDisplay(notification);
  const profileRowState = isProfileHref(navigateTo) ? profileModalBackgroundState(location) : undefined;
  const profileCtaState = cta?.to && isProfileHref(cta.to) ? profileModalBackgroundState(location) : undefined;
  const avatarSrc = avatarUrl || "";
  const avatarImgClass =
    avatarObjectFit === "contain"
      ? "h-full w-full object-contain p-0"
      : "h-full w-full object-cover";
  const isPanelVariant = variant === "panel";
  const rowShell =
    rowShellClassName ??
    (isPanelVariant
      ? MY_ZONE_ACTIVITY_PANEL_ROW_GRID_CLASS
      : getNotificationRowShellClass(isUnread, includeUnreadRing, isAlertRow));
  const rowGridShell =
    rowShellClassName ??
    (isPanelVariant
      ? MY_ZONE_ACTIVITY_PANEL_ROW_GRID_CLASS
      : getNotificationRowGridShellClass(isUnread, includeUnreadRing, isAlertRow));
  /** Titles wrap; row height follows content (shell uses min-h only). */
  const rowShellD = rowShell;
  const titleLineClamp = false;
  const useGridNotificationLayout = !rowShellClassName || isPanelVariant;
  const avatarBoxClass = isPanelVariant ? MY_ZONE_ACTIVITY_PANEL_AVATAR_CLASS : NOTIFICATION_AVATAR_CLASS;
  /** Logo / game-icon avatars use `contain`; only real sender photos get the sender's frame. */
  const isSenderAvatar = avatarObjectFit !== "contain";
  const avatarFrameSize = isPanelVariant ? "md" : "sm";
  const gapClass = isPanelVariant ? "gap-2" : "gap-1 sm:gap-1.5";
  const sideMetaClass = isPanelVariant ? "gap-1" : "gap-0.5 sm:gap-1";
  const timeClass = isPanelVariant
    ? MY_ZONE_ACTIVITY_PANEL_TIME_CLASS
    : "whitespace-nowrap text-[11px] leading-none text-[#c4b8d4] sm:text-[12px]";
  const ctaClass = isPanelVariant ? MY_ZONE_ACTIVITY_PANEL_CTA_CLASS : NOTIFICATION_CTA_PILL_CLASS;
  const titleLineClass = isPanelVariant ? MY_ZONE_ACTIVITY_PANEL_TITLE_LINE_CLASS : undefined;
  const titleUsernameClass = isPanelVariant ? MY_ZONE_ACTIVITY_PANEL_TITLE_USERNAME_CLASS : undefined;
  /** Natural width + truncate so short times (e.g. 23h) don’t leave a wide empty strip before the CTA. */
  const timeRailSlotClass = isPanelVariant
    ? "inline-block max-w-[4rem] shrink-0 truncate text-right tabular-nums sm:max-w-[4.5rem]"
    : "inline-block max-w-[4rem] shrink-0 truncate pr-0.5 text-right tabular-nums sm:max-w-[4.5rem] sm:pr-1";

  const fireActivate = () => {
    if (cta && !cta.openShopDrawer && !cta.openWalletHistory) {
      persistChallengeGameEntryFromNotification(notification);
    }
    onActivate?.();
  };

  const handleDrawerCtaClick = () => {
    fireActivate();
    if (cta?.openShopDrawer) {
      try {
        sessionStorage.setItem("c8_animate_wallet_on_open", "1");
      } catch (_) {
        /* ignore */
      }
      window.dispatchEvent(new CustomEvent("openShopDrawer"));
      return;
    }
    if (cta?.openWalletHistory) {
      window.dispatchEvent(new CustomEvent("openWalletHistoryDrawer"));
    }
  };

  const ctaControl =
    cta?.openShopDrawer || cta?.openWalletHistory ? (
      <button type="button" onClick={handleDrawerCtaClick} className={ctaClass}>
        {cta.label}
      </button>
    ) : cta ? (
      <ProfileLink to={cta.to} state={profileCtaState} onClick={fireActivate} className={ctaClass}>
        {cta.label}
      </ProfileLink>
    ) : null;

  return (
    <div className="w-full">
      {cta ? (
        useGridNotificationLayout ? (
          <div className={rowGridShell}>
            <ProfileLink
              to={navigateTo}
              state={profileRowState}
              onClick={fireActivate}
              className={`col-span-2 grid [grid-template-columns:subgrid] min-h-0 min-w-0 items-center text-left ${gapClass}`}
            >
              <NotificationRowAvatar
                boxClass={avatarBoxClass}
                src={avatarSrc}
                imgClass={avatarImgClass}
                notification={notification}
                xpWeekCrown={notification?.sender?.xp_week_crown}
                isSenderAvatar={isSenderAvatar}
                frameSize={avatarFrameSize}
              />
              <div className={`min-w-0 self-center overflow-x-hidden ${NOTIFICATION_MAIN_TEXT_PAD}`}>
                <NotificationTitleLine
                  parts={titleParts}
                  lineClamp={titleLineClamp}
                  lineClass={titleLineClass}
                  emphasizeUsernameClass={titleUsernameClass}
                />
              </div>
            </ProfileLink>
            <div className="flex shrink-0 items-center gap-1 self-center sm:gap-1.5">
              <div className={`flex shrink-0 items-center ${sideMetaClass}`}>
                {!hideTime ? (
                  <span className={`${timeClass} ${timeRailSlotClass}`}>
                    {formatTimeAgo(notification.created_at) || "\u00a0"}
                  </span>
                ) : null}
                {ctaControl}
              </div>
              {typeof onRemove === "function" ? <NotificationRemoveButton onRemove={onRemove} /> : null}
            </div>
          </div>
        ) : (
          <div className={rowShell}>
            <ProfileLink
              to={navigateTo}
              state={profileRowState}
              onClick={fireActivate}
              className={`flex min-h-0 min-w-0 flex-1 items-center text-left ${gapClass}`}
            >
              <NotificationRowAvatar
                boxClass={avatarBoxClass}
                src={avatarSrc}
                imgClass={avatarImgClass}
                notification={notification}
                xpWeekCrown={notification?.sender?.xp_week_crown}
                isSenderAvatar={isSenderAvatar}
                frameSize={avatarFrameSize}
              />
              <div
                className={`flex w-0 min-w-0 max-w-full flex-1 flex-col justify-start overflow-x-hidden ${NOTIFICATION_MAIN_TEXT_PAD}`}
              >
                <NotificationTitleLine
                  parts={titleParts}
                  lineClamp={titleLineClamp}
                  lineClass={titleLineClass}
                  emphasizeUsernameClass={titleUsernameClass}
                />
              </div>
            </ProfileLink>
            <div className={`flex shrink-0 items-center self-center ${sideMetaClass}`}>
              {!hideTime ? (
                <span className={`${timeClass} ${timeRailSlotClass}`}>
                  {formatTimeAgo(notification.created_at) || "\u00a0"}
                </span>
              ) : null}
              {ctaControl}
            </div>
            {typeof onRemove === "function" ? <NotificationRemoveButton onRemove={onRemove} /> : null}
          </div>
        )
      ) : (
        <div className={`${rowShellD} flex w-full min-w-0 items-center`}>
          <ProfileLink
            to={navigateTo}
            state={profileRowState}
            onClick={fireActivate}
            className={`flex min-w-0 flex-1 items-center ${gapClass}`}
          >
            <NotificationRowAvatar
              boxClass={avatarBoxClass}
              src={avatarSrc}
              imgClass={avatarImgClass}
              notification={notification}
              xpWeekCrown={notification?.sender?.xp_week_crown}
              isSenderAvatar={isSenderAvatar}
              frameSize={avatarFrameSize}
            />
            <div
              className={`flex w-0 min-w-0 max-w-full flex-1 flex-col justify-start overflow-x-hidden ${NOTIFICATION_MAIN_TEXT_PAD}`}
            >
              <NotificationTitleLine
                parts={titleParts}
                lineClamp={titleLineClamp}
                lineClass={titleLineClass}
                emphasizeUsernameClass={titleUsernameClass}
              />
            </div>
          </ProfileLink>
          {!hideTime ? (
            <div className={`ml-2 flex shrink-0 items-center self-center ${sideMetaClass}`}>
              <span className={`${timeClass} ${timeRailSlotClass}`}>
                {formatTimeAgo(notification.created_at) || "\u00a0"}
              </span>
            </div>
          ) : null}
          {typeof onRemove === "function" ? <NotificationRemoveButton onRemove={onRemove} /> : null}
        </div>
      )}
    </div>
  );
}
